import React, { useEffect, useMemo, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  ModalProvider,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'

import type { MenuResponse } from '@library/api-menu-experience'

import Loading from 'routes/Menu/Loading'
import { JustForYouTutorial } from 'routes/Menu/components/JustForYouTutorial'
import { MenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/MenuPreferencesTutorial'
import { useShouldShowMenuPreferencesTutorial } from 'routes/Menu/components/MenuPreferences/useShouldShowMenuPreferencesTutorial'
import { useBasket } from 'routes/Menu/domains/basket'
import { CollectionSlug } from 'routes/Menu/domains/collections'
import { DeliverySettingsDropdown } from 'routes/Menu/experiments/GoustoAnytime/DeliverySettingsDropdown'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'
import { MenuExperienceCategoryNav } from './MenuExperienceCategoryNav'
import { MenuExperienceRecipeDetailOverlay } from './MenuExperienceRecipeDetailOverlay'
import { MenuExperienceSubHeader } from './MenuExperienceSubHeader'
import { useMenuExperienceBasketRecipeTileSync } from './hooks/useMenuExperienceBasketRecipeTileSync'
import { useMenuExperienceNavigation } from './hooks/useMenuExperienceNavigation'
import { useMenuExperienceRecipeListTracking } from './hooks/useMenuExperienceRecipeListTracking'
import { useMenuExperienceTracking } from './hooks/useMenuExperienceTracking'
import { MenuBrandingContext, MenuBrandingProvider, getMenuBranding } from './menuBrandingContext'
import { MenuExperienceNavigationProvider } from './menuExperienceNavigationContext'
import { MenuExperienceRecipeDetailProvider } from './menuExperienceRecipeDetailContext'
import {
  RecipeGridCustomisationProvider,
  useRecipeGridCustomisation,
} from './recipeGridCustomisationContext'

type MenuExperiencePageProps = {
  location: {
    pathname?: string
    query: MenuQueryParams & Record<string, unknown> & { collection?: string }
    [key: string]: unknown
  }
  [key: string]: unknown
}

const ContentContainer = withCitrusTheme(Box, (theme) => ({
  margin: '1rem auto 0',
  padding: '0 0 1rem',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    padding: '0 30px 1rem',
  },
}))

const MENU_EXPERIENCE_CATEGORY_NAV_STYLE = {
  position: 'sticky',
  top: 'var(--delivery-settings-dropdown-height, 0px)',
} as const

const MenuExperienceBasketRecipeTileSynchronizer = ({
  menuContent,
  menuId,
  query,
}: {
  menuContent: MenuResponse['content']
  menuId: string
  query: MenuExperiencePageProps['location']['query']
}) => {
  const { selectRecipeGridOption } = useRecipeGridCustomisation()

  useMenuExperienceBasketRecipeTileSync(query, menuId, menuContent, selectRecipeGridOption)

  return null
}

const MenuExperienceTracker = ({
  basketCount,
  browseSelectionState,
  clearBrowseSelectionState,
  data,
  deliveryDate,
}: {
  basketCount: number
  browseSelectionState: Parameters<typeof useMenuExperienceTracking>[0]['browseSelectionState']
  clearBrowseSelectionState: () => void
  data: MenuResponse
  deliveryDate?: string
}) => {
  useMenuExperienceTracking({
    basketCount,
    browseSelectionState,
    clearBrowseSelectionState,
    data,
    deliveryDate,
  })

  return null
}

const MenuExperienceRecipeListTracker = ({ data }: { data: MenuResponse }) => {
  useMenuExperienceRecipeListTracking(data)

  return null
}

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const hasMountedRef = useRef(false)
  const lastLoadedMenuContextRef = useRef<Pick<MenuResponse, 'categoryId' | 'menuId' | 'menuUrn'>>()
  const {
    browseSelectionState,
    clearBrowseSelectionState,
    data,
    deliveryDate,
    error,
    links,
    selectCategory,
  } = useMenuExperienceNavigation(location.query, location.pathname)
  const { recipeCount, updateRecipePortionsByUuid } = useBasket()
  const navigationActions = useMemo(
    () => ({ categories: data?.categories, selectCategory }),
    [data?.categories, selectCategory],
  )
  const isCategoryNavVisible = links.some((link) => link.selected)
  const recommendationsCategory = data
    ? data.categories.find(({ slug }) => slug === CollectionSlug.Recommendations)
    : undefined
  const recommendationsCollection = data
    ? recommendationsCategory
      ? { shortTitle: recommendationsCategory.title }
      : null
    : undefined
  const showMenuPreferencesTutorial =
    useShouldShowMenuPreferencesTutorial(recommendationsCollection)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.query.collection])

  useEffect(() => {
    if (data) {
      lastLoadedMenuContextRef.current = {
        categoryId: data.categoryId,
        menuId: data.menuId,
        menuUrn: data.menuUrn,
      }
    }
  }, [data])

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    const loading = (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
    const lastLoadedMenuContext = lastLoadedMenuContextRef.current

    return lastLoadedMenuContext ? (
      <>
        <DeliverySettingsDropdown />
        <RecipeGridCustomisationProvider
          key={lastLoadedMenuContext.menuId}
          collectionId={lastLoadedMenuContext.categoryId}
          menuId={lastLoadedMenuContext.menuId}
          menuUrn={lastLoadedMenuContext.menuUrn}
        >
          {loading}
        </RecipeGridCustomisationProvider>
      </>
    ) : (
      loading
    )
  }

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper
  const menuBranding = getMenuBranding(data.content)
  const shouldGoBackToBrowse = data.type !== 'browse' && !isCategoryNavVisible

  const content = (
    <Box data-testid="menu-experience-page">
      <MenuExperienceTracker
        basketCount={recipeCount}
        browseSelectionState={browseSelectionState}
        clearBrowseSelectionState={clearBrowseSelectionState}
        data={data}
        deliveryDate={deliveryDate}
      />
      {data.type !== 'browse' && <MenuExperienceRecipeListTracker data={data} />}
      <MenuExperienceBasketRecipeTileSynchronizer
        menuContent={data.content}
        menuId={data.menuId}
        query={location.query}
      />
      <MenuExperienceSubHeader
        menuId={data.menuId}
        pathname={location.pathname}
        query={location.query}
        shouldGoBackToBrowse={shouldGoBackToBrowse}
      />
      <JustForYouTutorial recommendationsCollection={recommendationsCollection} />
      {showMenuPreferencesTutorial && <MenuPreferencesTutorial />}
      {isCategoryNavVisible && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={MENU_EXPERIENCE_CATEGORY_NAV_STYLE}>
          <MenuExperienceCategoryNav
            links={links}
            pathname={location.pathname}
            query={location.query}
          />
        </Box>
      )}
      <MenuBrandingProvider value={menuBranding}>
        <MenuExperienceRecipeDetailProvider
          collectionId={data.categoryId}
          collectionSlug={data.categorySlug}
          menuId={data.menuId}
          menuUrn={data.menuUrn}
          updateRecipePortions={updateRecipePortionsByUuid}
        >
          <MenuExperienceNavigationProvider value={navigationActions}>
            <MenuBrandingContext.Consumer>
              {({ backgroundColor, backgroundImageUrl, textColor }) => (
                <ContentContainer
                  data-testid="menu-experience-page-content"
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  maxWidth="1600px"
                  style={{
                    marginTop: backgroundColor || backgroundImageUrl ? 0 : undefined,
                    ...(backgroundColor ? { backgroundColor } : {}),
                    ...(textColor ? { color: textColor } : {}),
                    ...(backgroundImageUrl
                      ? {
                          backgroundImage: `url(${backgroundImageUrl})`,
                          backgroundPosition: 'top center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '100% auto',
                        }
                      : {}),
                  }}
                >
                  <ModalProvider>
                    <ScreenWrapper contentBlocks={data.content} collectionId={data.categoryId} />
                  </ModalProvider>
                </ContentContainer>
              )}
            </MenuBrandingContext.Consumer>
          </MenuExperienceNavigationProvider>
          <MenuExperienceRecipeDetailOverlay collectionId={data.categoryId} />
        </MenuExperienceRecipeDetailProvider>
      </MenuBrandingProvider>
    </Box>
  )

  return (
    <>
      <DeliverySettingsDropdown />
      <RecipeGridCustomisationProvider
        key={data.menuId}
        collectionId={data.categoryId}
        menuId={data.menuId}
        menuUrn={data.menuUrn}
      >
        {content}
      </RecipeGridCustomisationProvider>
    </>
  )
}
